import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {EventNotificationSubscriptionService} from '../../service/api/event-notification-subscription.service';
import {EventNotificationSubscriptionDto} from '../../model/dto/entity/event-notification-subscription.dto';
import {WebhookEventTypeEnum} from '../../enum/webhook-event-type.enum';

/**
 * Event Subscription Manager Component (F-033: Webhook and Event Notifications).
 * Provides UI for managing user subscriptions to campaign event notifications.
 *
 * Acceptance Criteria:
 * - Given member status changes, when event is configured, then event is published to message queue
 */
@Component({
    selector: 'ct-event-subscription-manager',
    templateUrl: './event-subscription-manager.component.html',
    standalone: false
})
export class EventSubscriptionManagerComponent implements OnChanges {

    @Input() userId: number;
    @Input() campaignId: number;
    @Input() campaignName: string;

    // User's subscriptions
    subscriptions: EventNotificationSubscriptionDto[] = [];

    // UI state
    loading: boolean = false;
    error: string = '';
    successMessage: string = '';

    // Form state
    selectedEventType: WebhookEventTypeEnum | null = null;
    deliveryChannel: string = 'email';

    // Event type options
    eventTypeOptions = [
        {value: WebhookEventTypeEnum.campaignActivated, label: 'Kampagne aktiviert'},
        {value: WebhookEventTypeEnum.campaignPaused, label: 'Kampagne pausiert'},
        {value: WebhookEventTypeEnum.campaignCompleted, label: 'Kampagne abgeschlossen'},
        {value: WebhookEventTypeEnum.memberAdded, label: 'Mitglied hinzugefügt'},
        {value: WebhookEventTypeEnum.memberStatusChanged, label: 'Mitgliedsstatus geändert'},
        {value: WebhookEventTypeEnum.memberConverted, label: 'Mitglied konvertiert'},
        {value: WebhookEventTypeEnum.memberRemoved, label: 'Mitglied entfernt'},
        {value: WebhookEventTypeEnum.refreshCompleted, label: 'Aktualisierung abgeschlossen'},
        {value: WebhookEventTypeEnum.refreshFailed, label: 'Aktualisierung fehlgeschlagen'}
    ];

    // Delivery channel options
    deliveryChannelOptions = [
        {value: 'email', label: 'E-Mail'},
        {value: 'in-app', label: 'In-App-Benachrichtigung'},
        {value: 'push', label: 'Push-Benachrichtigung'}
    ];

    constructor(private subscriptionService: EventNotificationSubscriptionService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes['userId'] || changes['campaignId']) && this.userId && this.campaignId) {
            this.loadSubscriptions();
        }
    }

    loadSubscriptions(): void {
        this.loading = true;
        this.subscriptionService.getSubscriptionsByCampaign(this.campaignId).subscribe({
            next: (subs) => {
                this.subscriptions = subs.filter(s => s.userDtoId === this.userId);
                this.loading = false;
            },
            error: (err) => {
                console.error('Failed to load subscriptions', err);
                this.error = 'Abonnements konnten nicht geladen werden.';
                this.loading = false;
            }
        });
    }

    /**
     * Creates a new event subscription.
     * F-033: Acceptance Criteria - "Given member status changes, when event is configured,
     * then event is published to message queue"
     */
    createSubscription(): void {
        if (this.selectedEventType === null) {
            this.error = 'Bitte wählen Sie einen Ereignistyp aus.';
            return;
        }

        this.loading = true;
        this.subscriptionService.createSubscription({
            userId: this.userId,
            campaignId: this.campaignId,
            eventType: this.selectedEventType,
            deliveryChannel: this.deliveryChannel
        }).subscribe({
            next: () => {
                this.successMessage = 'Abonnement erfolgreich erstellt.';
                this.loadSubscriptions();
                this.selectedEventType = null;
                this.loading = false;
            },
            error: (err) => {
                console.error('Failed to create subscription', err);
                this.error = 'Abonnement konnte nicht erstellt werden.';
                this.loading = false;
            }
        });
    }

    /**
     * Deactivates a subscription.
     */
    deactivateSubscription(subscriptionId: number): void {
        this.loading = true;
        this.subscriptionService.deactivateSubscription(subscriptionId).subscribe({
            next: () => {
                this.successMessage = 'Abonnement entfernt.';
                this.loadSubscriptions();
                this.loading = false;
            },
            error: (err) => {
                console.error('Failed to deactivate subscription', err);
                this.error = 'Abonnement konnte nicht entfernt werden.';
                this.loading = false;
            }
        });
    }

    /**
     * Subscribes to all events.
     */
    subscribeToAll(): void {
        this.loading = true;
        this.subscriptionService.subscribeToAllEvents(this.userId, this.campaignId, this.deliveryChannel).subscribe({
            next: () => {
                this.successMessage = 'Alle Ereignisse abonniert.';
                this.loadSubscriptions();
                this.loading = false;
            },
            error: (err) => {
                console.error('Failed to subscribe to all events', err);
                this.error = 'Alle Ereignisse konnten nicht abonniert werden.';
                this.loading = false;
            }
        });
    }

    /**
     * Unsubscribes from all events.
     */
    unsubscribeFromAll(): void {
        this.loading = true;
        this.subscriptionService.unsubscribeFromCampaign(this.userId, this.campaignId).subscribe({
            next: () => {
                this.successMessage = 'Alle Ereignisse abbestellt.';
                this.loadSubscriptions();
                this.loading = false;
            },
            error: (err) => {
                console.error('Failed to unsubscribe from all events', err);
                this.error = 'Alle Ereignisse konnten nicht abbestellt werden.';
                this.loading = false;
            }
        });
    }

    /**
     * Gets the label for an event type.
     */
    getEventTypeLabel(eventType: WebhookEventTypeEnum): string {
        const option = this.eventTypeOptions.find(o => o.value === eventType);
        return option ? option.label : eventType.toString();
    }

    /**
     * Gets the label for a delivery channel.
     */
    getDeliveryChannelLabel(channel: string): string {
        const option = this.deliveryChannelOptions.find(o => o.value === channel);
        return option ? option.label : channel;
    }

    /**
     * Clears messages.
     */
    clearMessages(): void {
        this.error = '';
        this.successMessage = '';
    }
}
